import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { IconAlertTriangle, IconSearch, IconSelector, IconX } from "@tabler/icons-react";
import { useEncoderCodeSearch } from "../../hooks/queries/useEncoder";
import type {
  AggregatedIcdCode,
  CodeSearchResult,
  EncoderIcdValidationResult,
} from "../../types/encoder";
import {
  cn,
  labelColorClass,
  labelStyle,
  sanitizeHighlightHtml,
} from "../../utils/encoder";

export const LabelChip: React.FC<{
  label: string;
  title?: string;
  wide?: boolean;
  colors?: Record<string, { background: string; color: string }>;
}> = ({ label, title, wide, colors }) => {
  const style = labelStyle(label, colors);
  return (
    <span
      title={title}
      className={cn(
        "inline-flex items-center justify-center rounded-sm px-1 py-px text-center text-[10px]",
        wide ? "min-w-[2.5rem] px-1.5" : "w-12",
        style ? "" : labelColorClass(label)
      )}
      style={style}
    >
      {label}
    </span>
  );
};

export const CodeLabels: React.FC<{
  result: Pick<CodeSearchResult, "labels" | "metadata">;
  colors?: Record<string, { background: string; color: string }>;
}> = ({ result, colors }) => {
  const labels = (result.labels ?? []).filter((label) => {
    const key = label.split("-")[0];
    return key !== "HCC" && key !== "Rx";
  });
  return (
    <div className="flex flex-wrap items-center gap-1">
      {labels.map((label) => (
        <LabelChip key={label} label={label} colors={colors} />
      ))}
      {result.metadata?.Rx?.v08 !== undefined && (
        <LabelChip
          label={`Rx-${result.metadata.Rx.v08}`}
          title="Version: v08"
          colors={colors}
        />
      )}
      {result.metadata?.HCC?.v24 !== undefined && (
        <LabelChip
          label={`HCC-${result.metadata.HCC.v24}`}
          title={`v24: ${result.metadata.HCC.v24}`}
          colors={colors}
        />
      )}
      {result.metadata?.HCC?.v28 !== undefined && (
        <LabelChip
          label={`HCC-${result.metadata.HCC.v28}`}
          title={`v28: ${result.metadata.HCC.v28}`}
          colors={colors}
        />
      )}
    </div>
  );
};

export const HighlightHtml: React.FC<{ html?: string; fallback: string }> = ({
  html,
  fallback,
}) => {
  const content = html?.trim() ? sanitizeHighlightHtml(html) : "";
  if (!content) return <>{fallback}</>;
  return (
    <span
      className="encoder-highlight"
      dangerouslySetInnerHTML={{ __html: content }}
    />
  );
};

export const EncoderDialog: React.FC<{
  open: boolean;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  wide?: boolean;
}> = ({ open, title, onClose, children, wide }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        className={cn(
          "flex max-h-[90vh] flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl",
          wide ? "w-[min(1100px,95vw)]" : "w-[min(720px,95vw)]"
        )}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <h2 className="text-base font-semibold text-slate-900">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-slate-500 hover:bg-slate-100"
            aria-label="Close"
          >
            <IconX size={16} />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-auto p-4">{children}</div>
      </div>
    </div>
  );
};

const instructionNoteItems = (value: unknown): string[] => {
  if (value == null) return [];
  if (typeof value === "string") {
    const text = value.trim();
    return text ? [text] : [];
  }
  if (!Array.isArray(value)) {
    if (typeof value === "object") {
      const record = value as Record<string, unknown>;
      return instructionNoteItems(
        record.text ?? record.note ?? record.description ?? record.value
      );
    }
    return [];
  }
  const out: string[] = [];
  const seen: Record<string, boolean> = {};
  for (let i = 0; i < value.length; i += 1) {
    const nested = instructionNoteItems(value[i]);
    for (let j = 0; j < nested.length; j += 1) {
      const item = nested[j];
      if (!item || seen[item]) continue;
      seen[item] = true;
      out.push(item);
    }
  }
  return out;
};

export const InstructionSection: React.FC<{
  title: string;
  items?: unknown;
  tone: "blue" | "red" | "yellow" | "sky" | "gray";
}> = ({ title, items, tone }) => {
  const list = instructionNoteItems(items);
  if (!list.length) return null;
  const tones = {
    blue: {
      dot: "bg-blue-500",
      card: "border-blue-200 bg-blue-50",
      bullet: "text-blue-500",
    },
    red: {
      dot: "bg-red-500",
      card: "border-red-200 bg-red-50",
      bullet: "text-red-500",
    },
    yellow: {
      dot: "bg-yellow-500",
      card: "border-yellow-200 bg-yellow-50",
      bullet: "text-yellow-500",
    },
    sky: {
      dot: "bg-sky-500",
      card: "border-sky-200 bg-sky-50",
      bullet: "text-fuchsia-500",
    },
    gray: {
      dot: "bg-gray-500",
      card: "border-gray-200 bg-gray-50",
      bullet: "text-gray-500",
    },
  }[tone];

  return (
    <div>
      <div className="mb-2 flex items-center gap-2">
        {tone === "red" ? (
          <IconX size={16} className="text-red-500" />
        ) : (
          <div className={cn("h-2 w-2 rounded-full", tones.dot)} />
        )}
        <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
      </div>
      <div className={cn("rounded-lg border p-3", tones.card)}>
        <ul className="space-y-1">
          {list.map((item, index) => (
            <li
              key={`${title}-${index}-${item.slice(0, 24)}`}
              className="flex items-start gap-2 text-sm text-gray-700"
            >
              <span className={cn("mt-1", tones.bullet)}>•</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export const IcdInstructionCards: React.FC<{ data?: AggregatedIcdCode | null }> = ({
  data,
}) => {
  if (!data) {
    return (
      <p className="py-4 text-center text-sm text-slate-500">
        No instructions available for this code
      </p>
    );
  }
  const codeFirst = instructionNoteItems(data.code_first);
  const excludes1 = instructionNoteItems(data.excludes1);
  const excludes2 = instructionNoteItems(data.excludes2);
  const useAdditional = instructionNoteItems(data.use_additional_code);
  const codeAlso = instructionNoteItems(data.code_also);
  const includeItems = instructionNoteItems([
    data.includes,
    data.inclusion_term,
  ]);
  const notes = instructionNoteItems(data.notes);
  const has =
    codeFirst.length ||
    excludes1.length ||
    excludes2.length ||
    useAdditional.length ||
    codeAlso.length ||
    includeItems.length ||
    notes.length;
  if (!has) {
    return (
      <p className="py-4 text-center text-sm text-slate-500">
        No instructions available for this code
      </p>
    );
  }
  return (
    <div className="space-y-4">
      <InstructionSection title="Code First" items={codeFirst} tone="blue" />
      <InstructionSection title="Excludes 1" items={excludes1} tone="red" />
      <InstructionSection title="Excludes 2" items={excludes2} tone="red" />
      <InstructionSection
        title="Use Additional Code"
        items={useAdditional}
        tone="yellow"
      />
      <InstructionSection title="Code Also" items={codeAlso} tone="sky" />
      <InstructionSection title="Includes" items={includeItems} tone="blue" />
      <InstructionSection title="Notes" items={notes} tone="gray" />
    </div>
  );
};

const INSTRUCTION_LABELS: Record<string, string> = {
  useAdditionalCode: "Use Additional Code",
  use_additional_code: "Use Additional Code",
  codeFirst: "Code First",
  code_first: "Code First",
  codeAlso: "Code Also",
  code_also: "Code Also",
  excludes1: "Excludes 1",
  excludes2: "Excludes 2",
  includes: "Includes",
  notes: "Notes",
};

export const formatIcdInstructionName = (instruction: string): string =>
  INSTRUCTION_LABELS[instruction] || instruction;

export const ValidationResultCard: React.FC<{
  result: EncoderIcdValidationResult;
  severity: "error" | "warning" | "info";
}> = ({ result, severity }) => {
  const tones =
    severity === "error"
      ? {
          card: "border-red-200 bg-red-50",
          title: "text-red-700",
          icon: "text-red-500",
          dot: "bg-red-500",
        }
      : severity === "warning"
        ? {
            card: "border-yellow-200 bg-yellow-50",
            title: "text-yellow-700",
            icon: "text-yellow-500",
            dot: "bg-yellow-500",
          }
        : {
            card: "border-blue-200 bg-blue-50",
            title: "text-blue-800",
            icon: "text-blue-500",
            dot: "bg-blue-500",
          };
  return (
    <div className={cn("rounded-lg border p-3", tones.card)}>
      <div className="flex items-start gap-2">
        {severity === "info" ? (
          <span className={cn("mt-2 h-2 w-2 shrink-0 rounded-full", tones.dot)} />
        ) : (
          <IconAlertTriangle size={16} className={cn("mt-0.5 shrink-0", tones.icon)} />
        )}
        <div className="min-w-0 flex-1">
          <p className={cn("text-sm font-medium", tones.title)}>
            {formatIcdInstructionName(result.instruction)}
          </p>
          {result.message ? (
            <p className="mt-1 text-sm text-gray-900">{result.message}</p>
          ) : null}
          {result.note ? (
            <p className="mt-1 text-xs italic text-gray-800">Note: {result.note}</p>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export const NativeSelect: React.FC<{
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
  className?: string;
  id?: string;
}> = ({ value, onChange, options, className, id }) => (
  <select
    id={id}
    value={value}
    onChange={(event) => onChange(event.target.value)}
    className={cn(
      "h-8 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-800",
      className
    )}
  >
    {options.map((option) => (
      <option key={option.value} value={option.value}>
        {option.label}
      </option>
    ))}
  </select>
);

export const useDebouncedValue = <T,>(value: T, delay: number): T => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
};

export const Field: React.FC<{
  label: string;
  children: React.ReactNode;
}> = ({ label, children }) => {
  const id = useId();
  return (
    <label className="flex min-w-0 flex-col gap-1 text-xs text-slate-500" htmlFor={id}>
      {label}
      <div id={id}>{children}</div>
    </label>
  );
};

export const Spinner: React.FC<{ label?: string }> = ({ label = "Loading..." }) => (
  <div className="flex items-center justify-center gap-2 py-8 text-sm text-slate-500">
    <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-slate-700" />
    {label}
  </div>
);

export const EncoderBrandEmpty: React.FC<{
  title: string;
  logoClassName?: string;
  className?: string;
}> = ({ title, logoClassName = "h-16 w-16", className }) => (
  <div className={cn("flex flex-1 items-center justify-center", className)}>
    <div className="flex flex-col items-center gap-4">
      <div className="flex items-center gap-2">
        <img src="/ai-bulwark-logo.svg" alt="" className={logoClassName} />
        <p className="text-5xl font-bold text-[#820ca5]/90">Bulwark Health</p>
      </div>
      <p className="text-3xl font-bold text-gray-700">{title}</p>
    </div>
  </div>
);

export const CodeSearchCombobox: React.FC<{
  year: number;
  type: string;
  placeholder?: string;
  onSelect: (result: CodeSearchResult) => void;
  accent?: "default" | "yellow";
  displayValue?: string;
}> = ({
  year,
  type,
  placeholder = "Search codes...",
  onSelect,
  accent,
  displayValue,
}) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const debounced = useDebouncedValue(query, 500);
  const search = useEncoderCodeSearch(debounced, type, year, {
    enabled: open && debounced.trim().length > 0,
  });
  const results: CodeSearchResult[] = search.data ?? [];
  const yellow = accent === "yellow";

  return (
    <div className="relative min-w-0 flex-1">
      <div
        className={cn(
          "relative flex h-11 min-w-0 items-center overflow-hidden rounded-lg border shadow-xs",
          yellow
            ? "border-2 border-yellow-500 bg-yellow-50"
            : "border-slate-300 bg-transparent"
        )}
      >
        <IconSearch
          size={16}
          className="pointer-events-none absolute left-3 text-slate-400"
        />
        <input
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && results[0]) {
              event.preventDefault();
              onSelect(results[0]);
              setQuery("");
              setOpen(false);
            }
            if (event.key === "Escape") setOpen(false);
          }}
          placeholder={displayValue || placeholder}
          className={cn(
            "h-full min-w-0 flex-1 bg-transparent py-0 pl-9 pr-8 text-base outline-none placeholder:text-slate-400",
            displayValue && !query ? "placeholder:text-slate-800" : ""
          )}
        />
        <IconSelector
          size={16}
          className="pointer-events-none absolute right-3 text-slate-400"
        />
      </div>
      {open && query.trim() && (
        <div className="absolute z-30 mt-1 flex h-[min(75vh,32rem)] w-full flex-col overflow-hidden rounded-md border border-slate-200 bg-white shadow-lg">
          <div className="min-h-0 flex-1 overflow-y-auto">
            {search.isFetching ? (
              <Spinner label="Searching..." />
            ) : results.length === 0 ? (
              <p className="px-3 py-4 text-sm text-slate-500">No codes found</p>
            ) : (
              results.map((result: CodeSearchResult) => (
                <button
                  key={`${result.type}-${result.code}`}
                  type="button"
                  className="flex w-full items-start gap-2 border-b px-3 py-3 text-left last:border-b-0 hover:bg-slate-50"
                  onClick={() => {
                    onSelect(result);
                    setQuery("");
                    setOpen(false);
                  }}
                >
                  <span className="font-mono text-sm">{result.code}</span>
                  <span className="min-w-0 flex-1 text-sm">{result.description}</span>
                  <span className="text-xs text-slate-500">{result.type}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const MergedCodeSearch: React.FC<{
  year: number;
  years: number[];
  type: string;
  types: string[];
  onYearChange: (year: number) => void;
  onTypeChange: (type: string) => void;
  onSelect: (result: CodeSearchResult | null) => void;
  selected?: CodeSearchResult | null;
  placeholder?: string;
}> = ({
  year,
  years,
  type,
  types,
  onYearChange,
  onTypeChange,
  onSelect,
  selected,
  placeholder = "Search codes...",
}) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const selectedLabel = selected
    ? `${selected.code}  ${selected.description}`
    : "";
  const searchQuery = query.trim() && query !== selectedLabel ? query : "";
  const debounced = useDebouncedValue(searchQuery, 500);
  const search = useEncoderCodeSearch(debounced, type, year, {
    enabled: open && debounced.trim().length > 0,
  });
  const results: CodeSearchResult[] = search.data ?? [];

  useEffect(() => {
    if (!selected) setQuery("");
  }, [selected]);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (event: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [open]);

  const pick = (result: CodeSearchResult) => {
    onSelect(result);
    setQuery(`${result.code}  ${result.description}`);
    setOpen(false);
  };

  return (
    <div className="relative min-w-0 flex-1" ref={wrapRef}>
      <div className="flex h-9 min-w-0 overflow-hidden rounded-md ring-2 ring-slate-300">
        <select
          value={String(year)}
          onChange={(event) => onYearChange(Number(event.target.value))}
          className="h-9 w-[4.5rem] shrink-0 border-0 border-r bg-white px-1.5 text-sm text-slate-800 outline-none"
        >
          {years.map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </select>
        <select
          value={type}
          onChange={(event) => onTypeChange(event.target.value)}
          className="h-9 w-[4.75rem] shrink-0 border-0 border-r bg-white px-1.5 text-sm text-slate-800 outline-none"
        >
          {types.map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </select>
        <div className="flex min-w-0 flex-1 items-center">
          <input
            value={query}
            onFocus={() => setOpen(true)}
            onChange={(event) => {
              const next = event.target.value;
              setQuery(next);
              setOpen(true);
              if (selected && next !== selectedLabel) onSelect(null);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter" && results[0]) {
                event.preventDefault();
                pick(results[0]);
              }
              if (event.key === "Escape") setOpen(false);
            }}
            placeholder={placeholder}
            className="h-9 min-w-0 flex-1 border-0 bg-white px-3 text-sm outline-none placeholder:text-slate-400"
          />
          <IconSelector size={16} className="mr-2 shrink-0 text-slate-400" />
        </div>
      </div>
      {open && searchQuery ? (
        <div className="absolute z-30 mt-1 w-full overflow-hidden rounded-md border bg-white shadow-lg">
          <div className="max-h-[50vh] overflow-auto">
            {search.isFetching ? (
              <Spinner label="Searching..." />
            ) : results.length === 0 ? (
              <p className="px-3 py-4 text-sm text-slate-500">No codes found</p>
            ) : (
              results.map((result: CodeSearchResult) => (
                <button
                  key={`${result.type}-${result.code}`}
                  type="button"
                  className="flex w-full items-start gap-2 border-b px-3 py-3 text-left last:border-b-0 hover:bg-slate-50"
                  onClick={() => pick(result)}
                >
                  <span className="font-mono text-sm">{result.code}</span>
                  <span className="min-w-0 flex-1 text-sm">{result.description}</span>
                  <span className="text-xs text-slate-500">{result.type}</span>
                </button>
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export const PcsCodeBoxes: React.FC<{
  selection: Record<number, string | null>;
  onApplyCode: (raw: string) => void;
  onChangeChar: (pos: number, char: string | null) => boolean;
}> = ({ selection, onApplyCode, onChangeChar }) => {
  const inputRefs = React.useRef<Array<HTMLInputElement | null>>([]);
  const focusPos = (pos: number) => {
    const input = inputRefs.current[pos - 1];
    if (!input) return;
    input.focus();
    input.select();
  };

  return (
    <div className="flex items-center gap-1.5">
      {[1, 2, 3, 4, 5, 6, 7].map((pos) => {
        const char = selection[pos];
        return (
          <input
            key={pos}
            ref={(node) => {
              inputRefs.current[pos - 1] = node;
            }}
            value={char ?? ""}
            placeholder="-"
            maxLength={7}
            spellCheck={false}
            aria-label={`PCS character ${pos}`}
            onFocus={(event) => event.currentTarget.select()}
            onChange={(event) => {
              const raw = event.target.value;
              if (raw.length > 1) {
                onApplyCode(raw);
                return;
              }
              if (!raw) {
                onChangeChar(pos, null);
                return;
              }
              const accepted = onChangeChar(pos, raw.toUpperCase());
              if (!accepted) {
                focusPos(pos);
                return;
              }
              if (pos < 7) focusPos(pos + 1);
            }}
            onPaste={(event) => {
              const pasted = event.clipboardData.getData("text");
              if (pasted.trim().length > 1) {
                event.preventDefault();
                onApplyCode(pasted);
              }
            }}
            onKeyDown={(event) => {
              if (event.key === "Backspace" && !char && pos > 1) {
                event.preventDefault();
                onChangeChar(pos - 1, null);
                focusPos(pos - 1);
              }
              if (event.key === "ArrowLeft" && pos > 1) {
                event.preventDefault();
                focusPos(pos - 1);
              }
              if (event.key === "ArrowRight" && pos < 7) {
                event.preventDefault();
                focusPos(pos + 1);
              }
            }}
            className={cn(
              "h-9 w-8 rounded border text-center font-mono text-xl font-bold outline-none placeholder:text-slate-400 focus:ring-2 focus:ring-green-500",
              char
                ? "border-green-600 bg-black text-green-400"
                : "border-slate-300 bg-slate-100 text-slate-500"
            )}
          />
        );
      })}
    </div>
  );
};

export const TabularNotes: React.FC<{
  notes?: {
    includes?: string[];
    inclusionTerm?: string[];
    excludes1?: string[];
    excludes2?: string[];
    codeFirst?: string[];
    useAdditionalCode?: string[];
    codeAlso?: string[];
    notes?: string[];
  } | null;
}> = ({ notes }) => {
  const mapped = useMemo(
    () =>
      notes
        ? {
            code_first: notes.codeFirst ?? [],
            excludes1: notes.excludes1 ?? [],
            excludes2: notes.excludes2 ?? [],
            use_additional_code: notes.useAdditionalCode ?? [],
            code_also: notes.codeAlso ?? [],
            includes: [...(notes.includes ?? []), ...(notes.inclusionTerm ?? [])],
            notes: notes.notes ?? [],
          }
        : null,
    [notes]
  );
  if (!mapped) return null;
  return <IcdInstructionCards data={{ ...mapped, code: "", description: "", inclusion_term: [], seven_chr_def: {}, seven_chr_note: [] }} />;
};
